import { t } from "./i18n";
import styled from "styled-components";
import { FiAlertTriangle } from "react-icons/fi";
import { reportEventBus } from "./ReportEvents";
import {
  WHITE,
  TEXT_LEAD,
  TINT_GREEN,
  GREEN_ACCENT,
  INK,
  BORDER_SUBTLE,
  SEMAPHORE,
  SEMAPHORE_WITH_NEUTRAL,
  SURFACE_CARD,
  TEXT_VALUE,
  type SemaphoreToneWithNeutral,
} from "../tokens";

// The report only ever reads the tier, so it carries the union rather than
// depending on either app's DTO module.
export type DueDiligenceTier = "basic" | "professional" | "premium";

// Duplicated from ExecutiveSummaryCockpit.tsx rather than imported, to avoid
// a circular import (that file imports this component). Keep both in sync.
const TIER_LEVEL: Record<DueDiligenceTier, number> = {
  basic: 1,
  professional: 2,
  premium: 3,
};

// Green and amber are the common case: a manageable risk stated next to its
// mitigant. Red is for one that blocks the operation until it is resolved —
// a report with no separate findings list carries those here too (Taracena
// opens with three, starting with the owner's liquidation). Neutral is the
// grey the design gives an area that is out of the report's scope at this
// tier ("Fiscal / Urbanístico / Mercado · No procede"), so it only ever
// appears on an area chip, never on a risk.
export type RiskSeverity = SemaphoreToneWithNeutral;

export interface RiskItem {
  title: string;
  mitigant: string;
  sourceSection?: string;
  sourceLabel?: string;
  severity: RiskSeverity;
}

export interface AreaStatus {
  name: string;
  label: string;
  severity: RiskSeverity;
  // Section number (e.g. "9") this area's status summarizes — makes the
  // chip clickable, same REPORT_SCROLL_TO_SECTION pattern the findings list
  // above already uses.
  sourceSection?: string;
}

export interface RisksData {
  items: RiskItem[];
  areas?: AreaStatus[];
}

const DOT_COLOR: Record<RiskSeverity, string> = {
  amber: SEMAPHORE_WITH_NEUTRAL.amber.fill,
  green: SEMAPHORE_WITH_NEUTRAL.green.fill,
  red: SEMAPHORE_WITH_NEUTRAL.red.fill,
  neutral: SEMAPHORE_WITH_NEUTRAL.neutral.fill,
};

const Section = styled.div`
  margin: 2.75rem 0 1.25rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-bottom: 1rem;
`;

const PanelIconBadge = styled.span`
  flex-shrink: 0;
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 0.56rem;
  background: ${TINT_GREEN[200]};
  color: ${SEMAPHORE.amber.text};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: ${TEXT_VALUE};
`;

// Dynamic N-item grid, same auto-fit pattern as the KPI/panels grids — the
// number of risk cards varies per case, this isn't a fixed 2-column table.
const RiskGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 0.81rem;
`;

const RiskCard = styled.div<{ $clickable?: boolean }>`
  text-align: left; width: 100%; font: inherit;
  ${(p) => p.$clickable && `cursor: pointer; &:hover { border-color: #cfe4d8; box-shadow: 0 4px 14px rgba(20,50,59,.07); } &:focus-visible { outline: 2px solid ${SEMAPHORE.green.text}; outline-offset: 3px; }`}
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 1rem 1.125rem;
  background: ${WHITE};
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.81rem;
`;

const RiskDot = styled.span<{ $severity: RiskSeverity }>`
  flex-shrink: 0;
  margin-top: 0.375rem;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: ${(p) => DOT_COLOR[p.$severity as RiskSeverity]};
`;

const RiskText = styled.div`
  font-size: 0.8125rem;
  line-height: 1.55;
  color: ${INK.soft};

  strong {
    color: ${TEXT_LEAD};
    font-weight: 600;
  }
`;

const MitigantLabel = styled.span`
  color: ${GREEN_ACCENT};
  font-weight: 600;
`;

const AreasBox = styled.div`
  margin-top: 1.625rem;
  background: ${SURFACE_CARD};
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.875rem;
  padding: 1rem 1.25rem;
`;

const AreasKicker = styled.div`
  font-size: 0.59rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${INK.label};
  margin-bottom: 0.75rem;
`;

const AreasRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem 1.375rem;
`;

const AreaChip = styled.span<{ $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.44rem;
  font-size: 0.75rem;
  color: ${INK.soft};
  ${(p) => p.$clickable && "cursor: pointer;"}

  ${(p) =>
    p.$clickable &&
    `&:hover {
      text-decoration: underline;
    }`}
`;

const AreaDot = styled.span<{ $severity: RiskSeverity }>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: ${(p) => DOT_COLOR[p.$severity as RiskSeverity]};
`;

const AreaName = styled.strong`
  color: ${TEXT_LEAD};
  font-weight: 600;
`;

const AreasFooter = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${BORDER_SUBTLE};
  font-size: 0.72rem;
  color: ${INK.quiet};
`;

interface Props {
  data: RisksData;
  sectionsCount?: number;
  tier?: DueDiligenceTier | null;
}

export const RisksAndMitigants = ({ data, sectionsCount, tier }: Props) => {
  if (!data.items.length) return null;

  const scopeText = [
    (!!sectionsCount || tier) && t("dueDiligence.finalReport.cockpit.scopeNote", { count: sectionsCount ?? 0 }),
    tier && t("dueDiligence.finalReport.cockpit.levelLabel", { level: TIER_LEVEL[tier] }),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Section>
      <SectionHeader>
        <PanelIconBadge>
          <FiAlertTriangle size={15} />
        </PanelIconBadge>
        <PanelTitle>{t("dueDiligence.finalReport.cockpit.risks.title")}</PanelTitle>
      </SectionHeader>

      <RiskGrid>
        {data.items.map((item, i) => (
          <RiskCard key={i} as={item.sourceSection ? "button" : "div"} type={item.sourceSection ? "button" : undefined} $clickable={!!item.sourceSection} onClick={item.sourceSection ? () => reportEventBus.emit("REPORT_SCROLL_TO_SECTION", `section-${item.sourceSection}`) : undefined}>
            <RiskDot $severity={item.severity} />
            <RiskText>
              <strong>{item.title}</strong>
              <br />
              <MitigantLabel>{t("dueDiligence.finalReport.cockpit.risks.mitigantLabel")} →</MitigantLabel> {item.mitigant}
              {item.sourceLabel && <div style={{ marginTop: 10, fontSize: 11, color: INK.quiet }}>› {item.sourceLabel}</div>}
            </RiskText>
          </RiskCard>
        ))}
      </RiskGrid>

      {data.areas && data.areas.length > 0 && (
        <AreasBox>
          <AreasKicker>{t("dueDiligence.finalReport.cockpit.risks.areasTitle")}</AreasKicker>
          <AreasRow>
            {data.areas.map((area, i) => {
              const clickable = !!area.sourceSection;
              return (
                <AreaChip
                  key={i}
                  $clickable={clickable}
                  onClick={clickable ? () => reportEventBus.emit("REPORT_SCROLL_TO_SECTION", `section-${area.sourceSection}`) : undefined}
                >
                  <AreaDot $severity={area.severity} />
                  <AreaName>{area.name}</AreaName> {area.label}
                </AreaChip>
              );
            })}
          </AreasRow>
          {scopeText && (
            <AreasFooter>{t("dueDiligence.finalReport.cockpit.risks.areasFooter", { scope: scopeText })}</AreasFooter>
          )}
        </AreasBox>
      )}
    </Section>
  );
};
